import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, SlidersHorizontal, ArrowUpDown } from 'lucide-react';
import { SortOption } from '@/hooks/useUserSorting';

interface SearchAndFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  sortField: SortOption;
  sortDirection: 'asc' | 'desc';
  onSortFieldChange: (field: SortOption) => void;
  onSortDirectionToggle: () => void;
  planTypeFilter: 'all' | 'free' | 'standard' | 'premium';
  onPlanTypeFilterChange: (planType: 'all' | 'free' | 'standard' | 'premium') => void;
  filteredUsersCount: number;
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  activeFiltersCount?: number;
}

const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  searchTerm,
  onSearchChange,
  sortField,
  sortDirection,
  onSortFieldChange,
  onSortDirectionToggle,
  planTypeFilter,
  onPlanTypeFilterChange,
  filteredUsersCount,
  filterOpen,
  onFilterOpenChange,
  activeFiltersCount = 0
}) => {
  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar por username, cidade ou email..."
          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/60"
        />
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Sort Field */}
        <Select value={sortField} onValueChange={(value) => onSortFieldChange(value as SortOption)}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Ordenar por" />
          </SelectTrigger>
          <SelectContent className="bg-black border-white/20 text-white">
            <SelectItem value="created_at">Data de Cadastro</SelectItem>
            <SelectItem value="last_login">Último Login</SelectItem>
            <SelectItem value="username">Username</SelectItem>
            <SelectItem value="role">Role</SelectItem>
            <SelectItem value="online_status">Status Online</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort Direction */}
        <Button
          variant="outline"
          size="icon"
          onClick={onSortDirectionToggle}
          className="shrink-0"
        >
          <ArrowUpDown className="h-4 w-4 text-foreground" />
        </Button>

        {/* Plan Type Filter */}
        <Select value={planTypeFilter} onValueChange={(value) => onPlanTypeFilterChange(value as any)}>
          <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Plano" />
          </SelectTrigger>
          <SelectContent className="bg-black border-white/20 text-white">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="free">Gratuito</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>

        {/* Advanced Filters Button */}
        <div className="relative">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onFilterOpenChange(!filterOpen)}
            className="shrink-0"
          >
            <SlidersHorizontal className="h-4 w-4 text-foreground" />
          </Button>
          {activeFiltersCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs"
              variant="destructive"
            >
              {activeFiltersCount}
            </Badge>
          )}
        </div>

        {/* Results Count */}
        <div className="ml-auto text-sm text-white/80">
          {filteredUsersCount} usuário{filteredUsersCount !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters;
